/**
 * Unificar varios envíos Courier en uno (reglas de UI / validación cliente).
 * Espejo de CRM-STAGE-BACK/utils/courierMergeShipments.js
 */

function normText(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

export function normAwb(value) {
  return normText(value).toUpperCase();
}

const BLOCKED_MERGE_ESTADOS = new Set(["CANCELADO", "ENTREGADO"]);

const STATUS_RANK = {
  PEDIDO: 0,
  PREPARANDO_ADUANAS: 1,
  EN_CURSO: 2,
  ENTREGADO: 3,
};

function statusRank(estado) {
  return STATUS_RANK[String(estado || "").toUpperCase()] ?? -1;
}

function toPositiveNumber(value) {
  if (value === "" || value == null) return null;
  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function scoreMergeCandidate(envio = {}) {
  let score = 0;
  if (normAwb(envio.NumeroAwb)) score += 100;
  if (normText(envio.CompaniaCourier)) score += 15;
  if (toPositiveNumber(envio.PesoBrutoKg)) score += 20;
  if (toPositiveNumber(envio.LargoCm)) score += 10;
  if (toPositiveNumber(envio.AnchoCm)) score += 5;
  if (toPositiveNumber(envio.AltoCm)) score += 5;
  if (toPositiveNumber(envio.Coste) || toPositiveNumber(envio.CosteReal))
    score += 8;
  score += Math.max(0, statusRank(envio.Estado)) * 5;
  return score;
}

export function pickMergeTarget(envios = [], preferredId = null) {
  const list = Array.isArray(envios) ? envios.filter(Boolean) : [];
  if (!list.length) return null;

  const preferred = Number(preferredId);
  if (Number.isFinite(preferred) && preferred > 0) {
    const hit = list.find((e) => Number(e.IdCourierControl) === preferred);
    if (hit) return hit;
  }

  return [...list].sort((a, b) => {
    const ds = scoreMergeCandidate(b) - scoreMergeCandidate(a);
    if (ds) return ds;
    return Number(a.IdCourierControl) - Number(b.IdCourierControl);
  })[0];
}

export function validateMergeCourierShipments(
  envios = [],
  { targetId = null } = {},
) {
  const list = Array.isArray(envios) ? envios.filter(Boolean) : [];
  if (list.length < 2) {
    return {
      ok: false,
      code: "MIN_TWO",
      messageKey: "mergeNeedTwo",
    };
  }

  for (const envio of list) {
    const st = String(envio.Estado || "").toUpperCase();
    if (BLOCKED_MERGE_ESTADOS.has(st)) {
      return {
        ok: false,
        code: "BLOCKED_STATUS",
        messageKey: "mergeBlockedStatus",
        status: st,
      };
    }
  }

  const directions = new Set(
    list.map((e) => String(e.Direccion || "").toUpperCase()),
  );
  if (directions.size > 1) {
    return { ok: false, code: "DIR_MISMATCH", messageKey: "mergeDirMismatch" };
  }

  const scopes = new Set(list.map((e) => String(e.Ambito || "").toUpperCase()));
  if (scopes.size > 1) {
    return {
      ok: false,
      code: "SCOPE_MISMATCH",
      messageKey: "mergeScopeMismatch",
    };
  }

  const destinos = [
    ...new Set(
      list.map((e) => normText(e.Destino).toLowerCase()).filter(Boolean),
    ),
  ];
  if (destinos.length > 1) {
    return {
      ok: false,
      code: "DEST_MISMATCH",
      messageKey: "mergeDestMismatch",
    };
  }

  const awbs = [
    ...new Set(list.map((e) => normAwb(e.NumeroAwb)).filter(Boolean)),
  ];
  if (awbs.length > 1) {
    return { ok: false, code: "AWB_MISMATCH", messageKey: "mergeAwbMismatch" };
  }

  const companies = [
    ...new Set(
      list
        .map((e) => normText(e.CompaniaCourier).toLowerCase())
        .filter(Boolean),
    ),
  ];
  if (companies.length > 1) {
    return {
      ok: false,
      code: "COURIER_MISMATCH",
      messageKey: "mergeCourierMismatch",
    };
  }

  const target = pickMergeTarget(list, targetId);
  if (!target?.IdCourierControl) {
    return { ok: false, code: "NO_TARGET", messageKey: "mergeNoTarget" };
  }

  const keepId = Number(target.IdCourierControl);
  const sourceIds = list
    .map((e) => Number(e.IdCourierControl))
    .filter((id) => Number.isFinite(id) && id > 0 && id !== keepId);

  return {
    ok: true,
    targetId: keepId,
    sourceIds,
    awb: awbs[0] || null,
  };
}

export function findSiblingIdsByAwb(rows = [], seedId) {
  const list = Array.isArray(rows) ? rows : [];
  const seed = list.find((r) => Number(r.IdCourierControl) === Number(seedId));
  if (!seed) return [];
  const awb = normAwb(seed.NumeroAwb);
  if (!awb) return [];
  return list
    .filter((r) => normAwb(r.NumeroAwb) === awb)
    .map((r) => Number(r.IdCourierControl))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function canSelectCourierForMerge(envio = {}) {
  const st = String(envio.Estado || "").toUpperCase();
  return !BLOCKED_MERGE_ESTADOS.has(st);
}
